/**
 * Redraws every still photo in the browser before it is uploaded as a message attachment, so that
 * none of its metadata (GPS, date, device…) leaves the phone (Tara, 07/10/2026); large photos are
 * shrunk on the way.
 *
 * Every doubt keeps the original file, metadata included (Mathieu, 07/10/2026): the promise always
 * resolves, with `reason` set to why the file was left untouched (or null when it was redrawn). The
 * module has no imports on purpose, so the same file can be loaded as-is in a real browser to check
 * its output.
 */

// A PNG up to this size, not scaled down, stays a PNG: screenshots keep sharp text and transparency.
export const PNG_KEEP_MAX_BYTES = 1000000;
// Pixel area cap rather than a long-side cap: tall scrolling screenshots stay readable.
export const SHRINK_MAX_PIXELS = 2560 * 1920;
// Above this, decoding alone risks the tab's memory on a phone: the original is sent.
export const SHRINK_MAX_SOURCE_PIXELS = 40000000;
// Safari's canvas side limit is unverified; an oversized canvas can come out blank without an error.
export const SHRINK_MAX_SIDE = 8192;
export const SHRINK_JPEG_QUALITY = 0.85;
// Best effort: synchronous decoding or encoding can delay the timer.
export const SHRINK_TIMEOUT_MS = 5000;
// Photos are shrunk one at a time: several full-size decodes at once can get a phone tab killed.
// A decode still running after the timeout keeps the queue until it ends, or at most this long.
export const SHRINK_QUEUE_RELEASE_MS = 2 * SHRINK_TIMEOUT_MS;

const SHRINKABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const TYPES_BY_EXTENSION = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
};
// The original name can carry the date (IMG_20261007_143022.jpg) and is sent as it is.
const NAMES_BY_OUTPUT_TYPE = { 'image/jpeg': 'photo.jpg', 'image/png': 'photo.png' };

/**
 * The declared type, to tell whether a file is worth reading. Some Android pickers give no type, a
 * generic one or a non-standard one (image/jpg) for a photo that isAllowedUpload accepts by its
 * extension: the extension then stands in. The first bytes decide afterwards (sniffImageType).
 *
 * @param {File} file
 * @returns {string}
 */
export const getEffectiveType = file => {
  const type = file?.type || '';
  if (SHRINKABLE_TYPES.includes(type)) {
    return type;
  }
  const name = file?.name || '';
  const lastDot = name.lastIndexOf('.');
  const extension = lastDot > 0 ? name.slice(lastDot + 1).toLowerCase() : '';
  return TYPES_BY_EXTENSION[extension] || type;
};

/**
 * Decides whether a file should be redrawn, to which size and in which format.
 * Without width and height, it only tells whether the file is worth decoding.
 *
 * @param {Object} params
 * @param {string} params.type MIME type (declared, or read from the first bytes)
 * @param {number} params.size size in bytes
 * @param {number} [params.width] displayed pixel width (EXIF orientation applied)
 * @param {number} [params.height] displayed pixel height (EXIF orientation applied)
 * @returns {{ shrink: boolean, reason: string|null, width?: number, height?: number, outputType?: string }}
 */
export const getShrinkPlan = ({ type, size, width, height }) => {
  if (!SHRINKABLE_TYPES.includes(type)) {
    return { shrink: false, reason: 'notShrinkableType' };
  }
  if (width == null && height == null) {
    return { shrink: true, reason: null };
  }
  if (!(width > 0 && height > 0)) {
    return { shrink: false, reason: 'unknownDimensions' };
  }

  const pixels = width * height;
  if (pixels > SHRINK_MAX_SOURCE_PIXELS) {
    return { shrink: false, reason: 'tooManyPixels' };
  }

  // Under the cap, the photo is still redrawn at its own size: that is what drops its metadata.
  const scale = Math.min(1, Math.sqrt(SHRINK_MAX_PIXELS / pixels));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));
  if (Math.max(targetWidth, targetHeight) > SHRINK_MAX_SIDE) {
    return { shrink: false, reason: 'sideTooLong' };
  }
  // WebP is written as JPEG too: older Safari cannot encode WebP.
  const keepsPng = type === 'image/png' && scale === 1 && size <= PNG_KEEP_MAX_BYTES;
  return {
    shrink: true,
    reason: null,
    width: targetWidth,
    height: targetHeight,
    outputType: keepsPng ? 'image/png' : 'image/jpeg',
  };
};

const readBytes = blob =>
  typeof blob.arrayBuffer === 'function'
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });

const chunkType = (bytes, offset) => String.fromCharCode(...bytes.subarray(offset, offset + 4));

// Only bounds the work on a corrupt file: a still PNG has a handful of chunks before its image data.
const PNG_MAX_CHUNKS_BEFORE_IDAT = 10000;

/**
 * Tells the real format from the first bytes. The declared type comes from the extension, which a
 * renamed file contradicts (an animated PNG named .jpg would lose its animation).
 *
 * @param {Blob} file
 * @returns {Promise<string|null>} 'image/jpeg', 'image/png', 'image/webp' or null
 */
export const sniffImageType = async file => {
  const bytes = new Uint8Array(await readBytes(file.slice(0, 12)));
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }
  if (bytes[0] === 0x89 && chunkType(bytes, 1).slice(0, 3) === 'PNG') {
    return 'image/png';
  }
  if (chunkType(bytes, 0) === 'RIFF' && chunkType(bytes, 8) === 'WEBP') {
    return 'image/webp';
  }
  return null;
};

/**
 * Tells whether a PNG (APNG) or WebP file is, or may be, animated, from its chunk headers: drawing
 * it on a canvas would keep a single frame. For a PNG, only the 8-byte chunk headers are read, so a
 * large metadata chunk before the image data does not count as an animation.
 *
 * @param {Blob} file
 * @param {string} [type] the type to go by (see sniffImageType), file.type by default
 * @param {Function} [isCancelled] true stops the walk (the original is then kept)
 * @returns {Promise<boolean>}
 */
export const mayBeAnimatedImage = async (file, type = file.type, isCancelled = () => false) => {
  if (type === 'image/webp') {
    const bytes = new Uint8Array(await readBytes(file.slice(0, 30)));
    // RIFF….WEBPVP8X, then the flags byte: bit 1 marks an animation.
    return chunkType(bytes, 12) === 'VP8X' && (bytes[20] & 0x02) !== 0;
  }
  if (type !== 'image/png') {
    return false;
  }
  // After the 8-byte signature, chunks are length (4) + type (4) + data + CRC (4).
  // An APNG declares acTL before its first IDAT.
  let offset = 8;
  for (let count = 0; count < PNG_MAX_CHUNKS_BEFORE_IDAT && !isCancelled(); count += 1) {
    const header = await readBytes(file.slice(offset, offset + 8));
    if (header.byteLength < 8) {
      // Truncated file: keep the original to be safe.
      return true;
    }
    const chunk = chunkType(new Uint8Array(header), 4);
    if (chunk === 'acTL') {
      return true;
    }
    if (chunk === 'IDAT' || chunk === 'IEND') {
      return false;
    }
    const length = new DataView(header).getUint32(0);
    // The PNG format caps a chunk at 2^31 - 1 bytes; a chunk running past the end is corrupt.
    if (length > 0x7fffffff || offset + 12 + length > file.size) {
      return true;
    }
    offset += 12 + length;
  }
  return true;
};

// Loading can be aborted by the timeout, so that the object URL is released.
const loadImage = (url, cancellation) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('imageLoadFailed'));
    cancellation.abort = () => {
      img.onload = null;
      img.src = '';
      reject(new Error('cancelled'));
    };
    img.src = url;
  });

// A browser without an encoder for the requested type answers with a PNG: the blob's real type
// names the file; anything else counts as a failure.
const canvasToBlob = (canvas, type) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob =>
        blob && NAMES_BY_OUTPUT_TYPE[blob.type]
          ? resolve(blob)
          : reject(
              new Error(blob ? `canvasBlobTypeUnexpected:${blob.type}` : 'canvasToBlobFailed')
            ),
      type,
      type === 'image/jpeg' ? SHRINK_JPEG_QUALITY : undefined
    );
  });

const shrink = async (file, cancellation) => {
  let url = null;
  let canvas = null;
  try {
    // A photo whose bytes are not JPEG, PNG or WebP (HEIC renamed .jpg…) is sent as it is.
    const type = await sniffImageType(file);
    if (!type) {
      return { file, reason: 'notShrinkableType' };
    }
    if (await mayBeAnimatedImage(file, type, () => cancellation.cancelled)) {
      return { file, reason: 'mayBeAnimated' };
    }
    if (cancellation.cancelled) {
      return { file, reason: 'timeout' };
    }

    url = URL.createObjectURL(file);
    const img = await loadImage(url, cancellation);
    const plan = getShrinkPlan({
      type,
      size: file.size,
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
    if (!plan.shrink) {
      return { file, reason: plan.reason };
    }

    // Decode off the main thread where the browser allows it, before drawing.
    if (typeof img.decode === 'function') {
      await img.decode();
    }
    // After a timeout the original is already on its way: skip the drawing and encoding work.
    if (cancellation.cancelled) {
      return { file, reason: 'timeout' };
    }

    canvas = document.createElement('canvas');
    canvas.width = plan.width;
    canvas.height = plan.height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('canvasContextUnavailable');
    }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    if (plan.outputType === 'image/jpeg') {
      // JPEG has no transparency: transparent areas become white, not black.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, plan.width, plan.height);
    }
    context.drawImage(img, 0, 0, plan.width, plan.height);

    // Kept even when heavier than the original: the copy is what has no metadata.
    const blob = await canvasToBlob(canvas, plan.outputType);
    const shrunk = new File([blob], NAMES_BY_OUTPUT_TYPE[blob.type], {
      type: blob.type,
      lastModified: file.lastModified,
    });
    return { file: shrunk, reason: null };
  } finally {
    if (url) {
      URL.revokeObjectURL(url);
    }
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
};

// Resolves the caller within SHRINK_TIMEOUT_MS; the returned promise settles when shrink() really
// ends (never rejects), which is what the queue waits for.
const runShrink = (file, resolve) => {
  let settled = false;
  const cancellation = { cancelled: false, abort: null };
  const settle = result => {
    if (!settled) {
      settled = true;
      clearTimeout(timer);
      resolve(result);
    }
  };
  // A result arriving after the timeout is ignored: the original has already been sent.
  const timer = setTimeout(() => {
    settle({ file, reason: 'timeout' });
    cancellation.cancelled = true;
    if (cancellation.abort) {
      cancellation.abort();
    }
  }, SHRINK_TIMEOUT_MS);
  return shrink(file, cancellation).then(settle, error => settle({ file, reason: 'error', error }));
};

let queueTail = Promise.resolve();

/**
 * Returns a redrawn copy of a still photo (without its metadata, shrunk when large), or the original.
 * Photos to decode wait for the previous ones; the time limit starts when their turn comes.
 *
 * @param {File} file the file picked by the member
 * @param {Object} [options]
 * @param {Function} [options.isWanted] checked when the turn comes: false skips the file
 * @returns {Promise<{ file: File, reason: string|null, error?: Error }>} never rejects
 */
export const shrinkImageForUpload = (file, { isWanted } = {}) => {
  if (!file) {
    return Promise.resolve({ file, reason: 'noFile' });
  }
  const candidate = getShrinkPlan({ type: getEffectiveType(file), size: file.size });
  if (!candidate.shrink) {
    // A photo saved under another name (a JPEG named .gif…) is still a photo: its bytes decide,
    // before the queue, so a real GIF or PDF still answers at once. A read that stalls keeps the
    // original after the usual time limit, and its late answer is ignored.
    const keep = { file, reason: candidate.reason };
    return new Promise(resolve => {
      let settled = false;
      const timer = setTimeout(() => {
        settled = true;
        resolve({ file, reason: 'timeout' });
      }, SHRINK_TIMEOUT_MS);
      const settle = result => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          resolve(result);
        }
      };
      sniffImageType(file).then(
        type => (settled ? null : settle(type ? queueShrink(file, isWanted) : keep)),
        error => settle({ file, reason: 'error', error })
      );
    });
  }
  return queueShrink(file, isWanted);
};

const queueShrink = (file, isWanted) => {
  const isSupported =
    typeof document !== 'undefined' &&
    typeof Image !== 'undefined' &&
    typeof File !== 'undefined' &&
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function';
  if (!isSupported) {
    return Promise.resolve({ file, reason: 'unsupported' });
  }

  return new Promise(resolve => {
    const task = () =>
      new Promise(release => {
        let cap = null;
        try {
          if (isWanted && !isWanted()) {
            resolve({ file, reason: 'notWanted' });
            release();
            return;
          }
          cap = setTimeout(release, SHRINK_QUEUE_RELEASE_MS);
          runShrink(file, resolve).then(() => {
            clearTimeout(cap);
            release();
          });
        } catch (error) {
          clearTimeout(cap);
          resolve({ file, reason: 'error', error });
          release();
        }
      });
    queueTail = queueTail.then(task).catch(() => {});
  });
};
